import React, { FC, useCallback, useMemo, useState } from 'react';
import { Feedback } from '@cubeyou-team/hx-react-components';
import { useDispatch, useSelector } from 'react-redux';

import { replaceTargetQuery } from 'src/actions';
import translations from 'src/lang';
import {
  StanleyQueryResponse,
  StanleyQuerySuggestion,
} from '../../../../../../api/types';
import { useStanleyQueryPanel } from '../../../../providers/StanleyQueryPanelContext';
import { StanleyQueryResultsSummary, StanleyQueryItem } from './components';
import { StanleyUseQueryModal } from './components/stanley-use-query-modal';
import { isTargetQueryEmpty } from './components/stanley-use-query-modal/isTargetQueryEmpty';
import { StanleyQueryFooter } from '../stanley-query-footer';

const { STANLEY_QUERY: translation } = translations['en-US'];

interface StanleyQueryResultsProps {
  data: StanleyQueryResponse;
}

export const StanleyQueryResults: FC<StanleyQueryResultsProps> = ({ data }) => {
  const dispatch = useDispatch();
  const { handleNewQuery, handleEditPrompt } = useStanleyQueryPanel();
  const targetSelected = useSelector(
    (state: { queryBuilder: { target_selected: string } }) =>
      state.queryBuilder.target_selected
  );
  const selectedTarget = useSelector(
    (state: {
      queryBuilder: {
        target_selected: string;
        targets: Record<
          string,
          { target_query?: { arguments_list?: unknown[] } }
        >;
      };
    }) => state.queryBuilder.targets[state.queryBuilder.target_selected]
  );
  const [useQuerySelection, setUseQuerySelection] =
    useState<StanleyQuerySuggestion | null>(null);

  const isSelectedTargetEmpty = useMemo(
    () => isTargetQueryEmpty(selectedTarget),
    [selectedTarget]
  );

  const handleOpenUseQuery = useCallback(
    (query: StanleyQuerySuggestion) => {
      if (isSelectedTargetEmpty && targetSelected) {
        dispatch(
          replaceTargetQuery(targetSelected, query.query_json.target_query)
        );
        return;
      }

      setUseQuerySelection(query);
    },
    [dispatch, isSelectedTargetEmpty, targetSelected]
  );

  const handleCloseUseQuery = useCallback(() => {
    setUseQuerySelection(null);
  }, []);

  return (
    <div>
      {data.strategic_warning !== null && (
        <div className="mb-4">
          <Feedback
            status="warning"
            message={`${translation.MODAL.WARNING.PREFIX}: ${data.strategic_warning}`}
            closable
          />
        </div>
      )}
      <div className="flex flex-col gap-4 pb-28">
        <StanleyQueryResultsSummary
          summary={data.summary}
          onEditPrompt={handleEditPrompt}
        />
        {data.queries?.map((query, index) => (
          <StanleyQueryItem
            key={`${query.title}-${index}`}
            query={query}
            index={index}
            onUseQuery={handleOpenUseQuery}
          />
        ))}
      </div>
      <div className="shadow-hx-footer-top absolute bottom-0 left-0 right-0 z-10 px-6 pb-16 bg-hx-light-dark">
        <StanleyQueryFooter
          isLoading={false}
          isNewQuery
          onPrimaryClick={handleNewQuery}
        />
      </div>
      <StanleyUseQueryModal
        isOpen={useQuerySelection != null}
        query={useQuerySelection}
        onClose={handleCloseUseQuery}
      />
    </div>
  );
};
